import { useTranslation } from 'react-i18next';
import type { Referral } from '@/hooks/useReferrals';
import { useReferralLetter } from '@/hooks/useReferrals';

/** Shared frame for letter rows and the henvisningsoplysninger fields. */
export const referralLetterFrameClass =
  'overflow-hidden rounded-lg border border-ds-line bg-ds-card shadow-card';

/** One labelled text block of a referral letter (label above, pre-wrapped value). */
export function LetterDetailBlock({
  label,
  value,
  last,
}: {
  label: string;
  value: string | null;
  last?: boolean;
}) {
  return (
    <div className={`px-6 py-5 ${last ? '' : 'border-b border-ds-line'}`}>
      <div className="mb-2">
        <span className="text-xs font-medium uppercase tracking-wide text-ds-muted">{label}</span>
      </div>
      <div className="whitespace-pre-wrap text-sm text-ds-text">{value ?? '—'}</div>
    </div>
  );
}

export interface ReferralLetterFields {
  referral_diagnosis: string | null;
  requested_examination: string | null;
  anamnese: string | null;
}

function formatFileDiagnosis(code: string | null, text: string | null): string | null {
  if (code && text) return `${code} — ${text}`;
  return code ?? text ?? null;
}

/**
 * The "Henvisnings detaljer" content: diagnosis, requested examination and anamnese.
 * Shared by the referral page (/henvisninger/:id) and the patient page's Henvisning tab.
 */
export function ReferralLetterDetails({
  referral,
}: {
  referral: ReferralLetterFields & Pick<Referral, 'id' | 'source' | 'referral_diagnosis_code'>;
}) {
  const { t } = useTranslation();
  const letterQuery = useReferralLetter(referral.id, referral.source === 'medcom');
  const fileRefer = letterQuery.data?.letter?.refer ?? null;
  const diagnosis =
    referral.source === 'medcom' && fileRefer && (fileRefer.code || fileRefer.text)
      ? formatFileDiagnosis(fileRefer.code, fileRefer.text)
      : formatFileDiagnosis(referral.referral_diagnosis_code, referral.referral_diagnosis) ??
        referral.referral_diagnosis;

  return (
    <section className={referralLetterFrameClass}>
      <LetterDetailBlock
        label={t('referrals.field_referral_diagnosis')}
        value={diagnosis}
      />
      <LetterDetailBlock
        label={t('referrals.field_requested_examination')}
        value={referral.requested_examination}
      />
      <LetterDetailBlock label={t('referrals.field_anamnese')} value={referral.anamnese} last />
    </section>
  );
}
